/**
 * InlineDdlMatViewRefreshBody — the embedded `Refresh` action body for
 * `CREATE MATERIALIZED VIEW`, mounted directly (no trigger, no Popover
 * chrome) for the Compact VQE popover's `compactDdlBodyArm`.
 *
 * Unlike every other VqeDdlEdit body, this one does NOT mutate the Monaco
 * buffer — it dispatches `REFRESH MATERIALIZED VIEW` (postgres) or
 * `BEGIN DBMS_MVIEW.REFRESH(...); END;` (oracle) directly against the live
 * connection.
 *
 * Engine gating: postgres + oracle supported; mssql / mysql / sqlite render
 * a muted one-line notice with a documented redirect tooltip instead. The
 * shared `MatViewRefreshFormBody` owns the engine-specific options form,
 * submit dispatch, and toast notifications — same shared-body pattern used
 * by the D2 view editor.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D8.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T4a/T5).
 */
import { useMemo } from 'react';

import type { QualifiedName } from '../../../sql-grammar/model';
import type { VqeDdlEditContext } from '../edit/editContext';
import { MatViewRefreshFormBody } from '../render/common/MatViewRefreshFormBody';
import {
  executeRefreshMaterializedView,
  getRefreshMaterializedViewUnsupportedTooltip,
  isRefreshMaterializedViewSupported,
  type ExecuteRefreshArgs,
} from '../services/refreshMaterializedView';
import {
  parseParkedViewStatement,
  type ParsedParkedView,
} from './parseParkedViewStatement';

const FALLBACK_NAME: QualifiedName = {
  name: '',
  raw: '',
  quoted: false,
  foldedName: '',
};

const NO_CONNECTION_TOOLTIP =
  'Connect the editor tab to a saved connection to refresh the materialized view.';

/** Shared scan: reused by the toolbar popover and the embedded body. */
function useParsedParkedMatView(
  editContext: VqeDdlEditContext,
  parsedOverride: ParsedParkedView | null | undefined,
): ParsedParkedView | null {
  return useMemo<ParsedParkedView | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedViewStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);
}

export interface InlineDdlMatViewRefreshBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-view scan. */
  readonly parsedOverride?: ParsedParkedView | null;
  /** Test seam — overrides `executeRefreshMaterializedView`. */
  readonly executeOverride?: (
    args: ExecuteRefreshArgs,
  ) => ReturnType<typeof executeRefreshMaterializedView>;
  /** Called after a successful commit (or Cancel) so the host can react. */
  readonly onDone?: () => void;
}

/**
 * Embedded body for the Compact VQE popover's `compactDdlBodyArm`, mounted
 * directly with no trigger/Popover chrome. The `no connection` gate surfaces
 * here as its own muted notice, since there is no button to disable.
 */
export function InlineDdlMatViewRefreshBody({
  editContext,
  parsedOverride,
  executeOverride,
  onDone,
}: InlineDdlMatViewRefreshBodyProps) {
  const engine = editContext.service.engineId;
  const parsed = useParsedParkedMatView(editContext, parsedOverride);

  const viewName = parsed?.name ?? FALLBACK_NAME;
  const supported = isRefreshMaterializedViewSupported(engine);
  const connectionId = editContext.connectionId ?? null;

  if (!supported) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-matview-refresh-unsupported"
      >
        {getRefreshMaterializedViewUnsupportedTooltip(engine)}
      </p>
    );
  }

  if (!connectionId) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-matview-refresh-unsupported"
      >
        {NO_CONNECTION_TOOLTIP}
      </p>
    );
  }

  return (
    <MatViewRefreshFormBody
      engine={engine}
      connectionId={connectionId}
      database={editContext.database ?? null}
      viewName={viewName}
      testId="inline-ddl-matview-refresh"
      onCommitted={() => onDone?.()}
      onCancel={() => onDone?.()}
      executeOverride={executeOverride}
    />
  );
}
